import { INFINITY as INF } from './internals';
import { reasonsOpampMarginal, reasonsOpampNotAllowed } from './opamp-ratings';
import {
  getPowerRecommendedOpAmpRequirementSpec,
  getNoiseAndVoltageRangeOpAmpRequirementSpec,
  getAllowedOpAmpRequirementSpec,
} from './limit-specs';
import { getNoiseOpAmpTopPick, getPowerOpAmpTopPick, getVoltageRangeOpAmpTopPick } from './opamp-top-pick';

export const INFINITY = INF;

export function evaluateOpAmps(opAmps, circuits, vsPlus, vsMinus, optimization, stopbandFrequency) {
  var recommendationSpec =
    optimization === 'power'
      ? getPowerRecommendedOpAmpRequirementSpec(stopbandFrequency)
      : getNoiseAndVoltageRangeOpAmpRequirementSpec(stopbandFrequency);
  var allowedSpec = getAllowedOpAmpRequirementSpec(stopbandFrequency);
  var recommendedLimits = _getOverallLimits(circuits, recommendationSpec);
  var allowedLimits = _getOverallLimits(circuits, allowedSpec);

  return opAmps.map((amp) => {
    let reasonsNotAllowed = reasonsOpampNotAllowed(vsPlus, vsMinus, allowedLimits, false, amp);
    let reasonsMarginal = reasonsNotAllowed.length === 0 ? reasonsOpampMarginal(recommendedLimits, amp) : [];
    return reasonsNotAllowed.length > 0
      ? { partNumber: amp.partNumber, status: 'notAllowed', reasons: reasonsNotAllowed }
      : reasonsMarginal.length > 0
      ? { partNumber: amp.partNumber, status: 'allowed', reasons: reasonsMarginal }
      : { partNumber: amp.partNumber, status: 'recommended', reasons: [] };
  });
}

export function getAppropriateOpAmps(
  opAmps,
  circuits,
  vsPlus,
  vsMinus,
  optimization,
  stopbandFrequency,
  applyAdditionalCriteria
) {
  var recommendationSpec =
    optimization === 'power'
      ? getPowerRecommendedOpAmpRequirementSpec(stopbandFrequency)
      : getNoiseAndVoltageRangeOpAmpRequirementSpec(stopbandFrequency);
  var allowedSpec = getAllowedOpAmpRequirementSpec(stopbandFrequency);
  var recommendedLimits = _getOverallLimits(circuits, recommendationSpec);
  var allowedLimits = _getOverallLimits(circuits, allowedSpec);

  var root10 = Math.sqrt(10);
  var initialMaxCurrent = optimization === 'noise' ? 5e-3 : 10e-6;

  // Before we iterate, restrict the list to parts we can even consider
  const allowedList = opAmps.filter((amp) => {
    // Criterion: the circuit must work
    let reasonsNotAllowed = reasonsOpampNotAllowed(vsPlus, vsMinus, allowedLimits, false, amp, applyAdditionalCriteria);
    return reasonsNotAllowed.length === 0;
  });

  const recommendedList = allowedList.filter((amp) => {
    // Criterion: the circuit must work under recommended conditions
    let reasonsMarginal = reasonsOpampMarginal(recommendedLimits, amp, applyAdditionalCriteria);
    return reasonsMarginal.length === 0;
  });

  //Handy debug code
  // prettier-ignore
  // {
  //   const originalListPartNumbers = opAmps.map(function (element) {// eslint-disable-line no-unused-vars
  //     return element.partNumber;
  //   });
  //   const allowedListPartNumbers = allowedList.map(function(element){ // eslint-disable-line no-unused-vars
  //     return element.partNumber;
  //   });
  //   const recommendedListPartNumbers = recommendedList.map(function(element){ // eslint-disable-line no-unused-vars
  //     return element.partNumber;
  //   });
  //   const sortedRecommendedListBySupplyCurrent = [...recommendedList];
  //   sortedRecommendedListBySupplyCurrent.sort((a,b) => {
  //     return a.supplyCurrent - b.supplyCurrent;
  //   });
  //   const sortedRecommendedListCurrents = sortedRecommendedListBySupplyCurrent.map(function(element){ // eslint-disable-line no-unused-vars
  //     return element.supplyCurrent;
  //   });
  //   const sortedRecommendedListPartNumbers = sortedRecommendedListBySupplyCurrent.map(function(element){ // eslint-disable-line no-unused-vars
  //     return element.partNumber;
  //   });
  //   const dummyVariableForBreakpoint = 0; // eslint-disable-line no-unused-vars
  // }
  //End debug code

  let opampList = [];
  // Will start with small current range, then gradually increase current range until we find op amps that meet the criteria
  for (
    let minCurrent = 0, maxCurrent = initialMaxCurrent;
    maxCurrent <= 0.1 && opampList.length === 0; // Assume we have no parts with supply current > 100 mA
    minCurrent = maxCurrent, maxCurrent = root10 * maxCurrent
  ) {
    opampList = recommendedList.filter((amp) => {
      return (
        amp.supplyCurrent !== null && amp.supplyCurrent <= maxCurrent && amp.supplyCurrent > minCurrent && !amp.nonPick
      );
    });
  }

  return opampList;
}

export function getTopOpAmp(
  opAmps,
  circuits,
  vsPlus,
  vsMinus,
  optimization,
  noiseResistance,
  totalGain,
  stopbandFrequency,
  applyAdditionalCriteria
) {
  var result = null;
  let opAmpsList = getAppropriateOpAmps(
    opAmps,
    circuits,
    vsPlus,
    vsMinus,
    optimization,
    stopbandFrequency,
    applyAdditionalCriteria
  );

  switch (optimization) {
    case 'noise':
      result = getNoiseOpAmpTopPick(opAmpsList, noiseResistance);
      break;
    case 'power':
      result = getPowerOpAmpTopPick(opAmpsList);
      break;
    case 'voltageRange':
      result = getVoltageRangeOpAmpTopPick(opAmpsList, totalGain, vsMinus, vsPlus);
      break;
  }

  return result;
}

function _getOverallLimits(circuits, opAmpRequirementSpec) {
  var circuitLimits = [];
  for (let i = 0; i < circuits.length; i++) {
    circuitLimits.push(_getCircuitIdealOpAmp(circuits[i], opAmpRequirementSpec));
  }

  var result = {
    maxGbw: Math.max.apply(
      Math,
      circuitLimits.map((item) => {
        return item.gbw;
      })
    ),
    minIBias: Math.min.apply(
      Math,
      circuitLimits.map((item) => {
        return item.iBias;
      })
    ),
    minROutOverGBW: Math.min.apply(
      Math,
      circuitLimits.map((item) => {
        return item.rOutOverGBW;
      })
    ),
    minSpecialGain: Math.min.apply(
      Math,
      circuitLimits.map((item) => {
        return item.specialGain;
      })
    ),
    maxSlewRate: Math.max.apply(
      Math,
      circuitLimits.map((item) => {
        return item.slewRate;
      })
    ),
  };

  return result;
}

function _getCircuitIdealOpAmp(circuit, opAmpRequirementSpec) {
  var limits = {};
  switch (circuit.type) {
    case 'bandpass':
      if (circuit.implementation == 'multipleFeedback') {
        limits = _getMultipleFeedbackBandpassIdealOpAmp(circuit, opAmpRequirementSpec);
      } else if (circuit.implementation == 'activeInductor') {
        limits = _getInductorBandPassIdealOpAmp(circuit, opAmpRequirementSpec);
      }
      break;
    case 'highpass':
      if (circuit.implementation == 'multipleFeedback') {
        limits = _getMultipleFeedbackHighPassIdealOpAmp(circuit, opAmpRequirementSpec);
      } else if (circuit.implementation == 'activeFirstOrder') {
        limits = _getFirstOrderHighPassIdealOpAmp(circuit, opAmpRequirementSpec);
      } else if (circuit.implementation == 'sallenKey') {
        limits = _getSallenKeyHighPassIdealOpAmp(circuit, opAmpRequirementSpec);
      } else if (circuit.implementation == 'activeInductor') {
        limits = _getInductorHighPassIdealOpAmp(circuit, opAmpRequirementSpec);
      }
      break;
    case 'lowpass':
      if (circuit.implementation == 'multipleFeedback') {
        limits = _getMultipleFeedbackLowPassIdealOpAmp(circuit, opAmpRequirementSpec);
      } else if (circuit.implementation == 'activeFirstOrder') {
        limits = _getFirstOrderLowPassIdealOpAmp(circuit, opAmpRequirementSpec);
      } else if (circuit.implementation == 'sallenKey') {
        limits = _getSallenKeyLowPassIdealOpAmp(circuit, opAmpRequirementSpec);
      } else if (circuit.implementation == 'activeInductor') {
        limits = _getInductorLowPassIdealOpAmp(circuit, opAmpRequirementSpec);
      }
      break;
  }

  return limits;
}

function _getMultipleFeedbackBandpassIdealOpAmp(circuit, opAmpRequirementSpec) {
  var iBiasResistance = circuit.r5;
  var iBias = opAmpRequirementSpec.dcOutputVoltageFromIBias / iBiasResistance / circuit.gain;

  var allowedError = opAmpRequirementSpec.allowedError;
  var fb = _bandpassMFBFeedbackForRecommendations(circuit.q, circuit.gain);
  var epsilon = (allowedError + 1) * (allowedError + 1) - 1;
  var gbw = ((2 * fb.q0) / (epsilon / circuit.fp)) * (1 / ((1 - fb.k) * (1 - fb.k)));

  var rOutOverGbw = INF;
  var rOut = rOutOverGbw * gbw;

  return {
    gbw: gbw,
    iBias: iBias,
    rOutOverGBW: INF,
    specialGain: 1,
    slewRate: 0,
    rOut: rOut,
  };
}

function _getInductorBandPassIdealOpAmp(circuit, opAmpRequirementSpec) {
  var gbw = opAmpRequirementSpec.inductorMultiplier * circuit.fp * circuit.gain;

  if (circuit.fp * circuit.gain > 100e6) {
    gbw = gbw / 2;
  }

  var iBiasResistance = 0;
  var iBias = opAmpRequirementSpec.dcOutputVoltageFromIBias / iBiasResistance / circuit.gain;
  var specialGain = 1 + circuit.r4 / circuit.r3;
  var rOutOverGBW = INF;
  var rOut = rOutOverGBW * gbw;

  return {
    gbw: gbw,
    iBias: iBias,
    rOutOverGBW: rOutOverGBW,
    specialGain: specialGain,
    slewRate: 0,
    rOut: rOut,
  };
}

function _getMultipleFeedbackHighPassIdealOpAmp(circuit, opAmpRequirementSpec) {
  var iBiasResistance = circuit.r2;
  var gain = circuit.c1 / circuit.c3;
  var iBias = opAmpRequirementSpec.dcOutputVoltageFromIBias / iBiasResistance / gain;

  var specialGain = 1 + circuit.c1 / circuit.c3;
  var gbw = opAmpRequirementSpec.fMultiplier * circuit.fp * gain * Math.max(circuit.q, 1);
  var rOutOverGbw = INF;
  var rOut = rOutOverGbw * gbw;

  return {
    gbw: gbw,
    iBias: iBias,
    rOutOverGBW: INF,
    specialGain: specialGain,
    slewRate: 0,
    rOut: rOut,
  };
}

function _getFirstOrderHighPassIdealOpAmp(circuit, opAmpRequirementSpec) {
  var gbw = opAmpRequirementSpec.fMultiplier * circuit.fp * circuit.gain;

  var iBiasResistance = circuit.r1;
  var iBias = opAmpRequirementSpec.dcOutputVoltageFromIBias / iBiasResistance / circuit.gain;
  var specialGain = 1 + circuit.r4 / circuit.r3;
  var rOutOverGBW = INF;
  var rOut = rOutOverGBW * gbw;

  return {
    gbw: gbw,
    iBias: iBias,
    rOutOverGBW: rOutOverGBW,
    specialGain: specialGain,
    slewRate: 0,
    rOut: rOut,
  };
}

function _getSallenKeyHighPassIdealOpAmp(circuit, opAmpRequirementSpec) {
  var fMultiplier = opAmpRequirementSpec.fMultiplier;
  var gbw = 2 * fMultiplier * circuit.fp * circuit.gain * Math.max(circuit.q, 1);

  var iBiasResistance = circuit.r2;
  var iBias = opAmpRequirementSpec.dcOutputVoltageFromIBias / iBiasResistance / circuit.gain;
  var specialGain = 1 + circuit.r4 / circuit.r3;
  var rOutOverGBW = INF;
  var rOut = rOutOverGBW * gbw;

  return {
    gbw: gbw,
    iBias: iBias,
    rOutOverGBW: rOutOverGBW,
    specialGain: specialGain,
    slewRate: 0,
    rOut: rOut,
  };
}

function _getInductorHighPassIdealOpAmp(circuit, opAmpRequirementSpec) {
  var gbw = opAmpRequirementSpec.inductorMultiplier * circuit.fp * circuit.gain;

  var iBiasResistance = circuit.r1;
  var iBias = opAmpRequirementSpec.dcOutputVoltageFromIBias / iBiasResistance / circuit.gain;
  var specialGain = 1 + circuit.r4 / circuit.r3;
  var rOutOverGBW = INF;
  var rOut = rOutOverGBW * gbw;

  return {
    gbw: gbw,
    iBias: iBias,
    rOutOverGBW: rOutOverGBW,
    specialGain: specialGain,
    slewRate: 0,
    rOut: rOut,
  };
}

function _getMultipleFeedbackLowPassIdealOpAmp(circuit, opAmpRequirementSpec) {
  var parallelR1R5 = (circuit.r1 * circuit.r5) / (circuit.r1 + circuit.r5);
  var gain = circuit.r5 / circuit.r1;
  var iBiasResistance = circuit.r2 + parallelR1R5;
  var iBias = opAmpRequirementSpec.dcOutputVoltageFromIBias / iBiasResistance / gain;

  var allowedError = opAmpRequirementSpec.allowedError;
  var numerator =
    circuit.fp * (-2 * circuit.q * gain + Math.sqrt((circuit.c1 * circuit.r5) / (circuit.c2 * circuit.r2)));
  var denominator = 2 * allowedError;
  var gbw = (numerator * gain) / denominator;

  var rOutOverGbw = INF;
  var rOut = rOutOverGbw * gbw;

  return {
    gbw: gbw,
    iBias: iBias,
    rOutOverGBW: INF,
    specialGain: 1,
    slewRate: 0,
    rOut: rOut,
  };
}

function _getFirstOrderLowPassIdealOpAmp(circuit, opAmpRequirementSpec) {
  var attenuation = 1 - opAmpRequirementSpec.allowedError;
  var fCutOff = circuit.fp / Math.sqrt(1 / (attenuation * attenuation) - 1);
  var gbw = fCutOff * circuit.gain;

  var iBiasResistance = circuit.r1;
  var iBias = opAmpRequirementSpec.dcOutputVoltageFromIBias / iBiasResistance / circuit.gain;
  var specialGain = 1 + circuit.r4 / circuit.r3;
  var rOutOverGBW = INF;
  var rOut = rOutOverGBW * gbw;

  return {
    gbw: gbw,
    iBias: iBias,
    rOutOverGBW: rOutOverGBW,
    specialGain: specialGain,
    slewRate: 0,
    rOut: rOut,
  };
}

function _getSallenKeyLowPassIdealOpAmp(circuit, opAmpRequirementSpec) {
  var allowedError = opAmpRequirementSpec.allowedError;
  var epsilon = (allowedError + 1) * (allowedError + 1) - 1;
  var numerator = circuit.gain * circuit.gain;
  var denominator = circuit.c2 * circuit.r2 * 2 * Math.PI * epsilon;
  var gbw = numerator / denominator;

  var iBiasResistance = circuit.r1 + circuit.r2;
  var iBias = opAmpRequirementSpec.dcOutputVoltageFromIBias / iBiasResistance / circuit.gain;
  var specialGain = 1 + circuit.r4 / circuit.r3;

  var attenuation =
    (opAmpRequirementSpec.stopbandFrequency / circuit.fp) * (opAmpRequirementSpec.stopbandFrequency / circuit.fp);
  var f = opAmpRequirementSpec.stopbandFrequency * opAmpRequirementSpec.rOutFreqMultiplier;
  var result = ((1 / f) * circuit.r1) / (attenuation - circuit.r1 / circuit.r2 - 1);

  if (result < 0) {
    result = INF;
  }
  var rOutOverGBW = result;
  var rOut = rOutOverGBW * gbw;

  return {
    gbw: gbw,
    iBias: iBias,
    rOutOverGBW: rOutOverGBW,
    specialGain: specialGain,
    slewRate: 0,
    rOut: rOut,
  };
}

function _getInductorLowPassIdealOpAmp(circuit, opAmpRequirementSpec) {
  var gbw = opAmpRequirementSpec.inductorMultiplier * circuit.fp * circuit.gain;

  var iBiasResistance = circuit.r1;
  var iBias = opAmpRequirementSpec.dcOutputVoltageFromIBias / iBiasResistance / circuit.gain;
  var specialGain = 1 + circuit.r4 / circuit.r3;
  var rOutOverGBW = INF;
  var rOut = rOutOverGBW * gbw;

  return {
    gbw: gbw,
    iBias: iBias,
    rOutOverGBW: rOutOverGBW,
    specialGain: specialGain,
    slewRate: 0,
    rOut: rOut,
  };
}

let _bandpassMFBFeedbackForRecommendations = function (stageQ, gain) {
  let a = gain / (2 * stageQ * stageQ);
  if (stageQ < 10 && a < 1) {
    return { q0: stageQ, b: a, k: 0, g: 0 }; // k here is probably very wrong, should be INF?
  } else {
    {
      //According to Van Valkenburg textbook, this is close to an optimal at 1.5,
      //Paul Fleischer paper indicates something slightly higher might be better
      //Yet my experiments indicate higher numbers are better still for lowest tolerance issues.
      //Only reason I haven't set it higher is because the ratio between R1 and R2  gets to be really big.  Choosing 5 keeps the ratio at 100.
      var qHat = 5;
      let q0 = 0;
      if (a < 1) {
        q0 = qHat;
      } else {
        let aMax = 0.9999999999;
        q0 =
          (-(aMax + gain) + Math.sqrt((aMax + gain) * (aMax + gain) - 8 * aMax * aMax * stageQ * stageQ)) /
          (-4 * aMax * stageQ);
      }
      let alpha = (1 / (2 * q0 * q0)) * (1 - q0 / stageQ);
      let k = alpha / (1 + alpha);
      let h = gain * (q0 / stageQ) * (1 - k);
      let b = h / 2 / (q0 * q0);
      return { q0: q0, b: b, k: k, g: 1 / k };
    }
  }
};
